import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'
import { AxiosError, AxiosResponse } from 'axios'

import {
  useB2COneTimePaymentsTableState,
  useB2CRegistryPaymentsTableState,
  useB2CState,
} from '@/stores/slices/b2c-slice'
import { useB2CRegistryState } from '@/stores/slices/b2c-registry-slice.ts'

import { TableData } from '../types.ts'
import {
  B2CPaymentsRequest,
  B2CRegistriesRequest,
  B2CRegistryConfirmErrorResponse,
  B2CRegistryConfirmForm,
  B2CRegistryConfirmSuccessResponse,
  B2CRegistryInitErrorResponse,
  B2CRegistryInitSuccessResponse,
  B2CTransferCheckErrorResponse,
  B2CTransferCheckSuccessResponse,
  B2CTransferConfirmErrorResponse,
  B2CTransferConfirmForm,
  B2CTransferConfirmResponse,
  B2CTransferRequestForm,
  SbpParticipant,
} from '../@types/b2c.types.ts'
import { omitRequestParams } from '../utils.ts'

import { apiService } from './root.ts'

export const B2C_ROOT = '/sbp-b2c'
export const B2C_TRANSFER_ROOT = `${B2C_ROOT}/transfer`
const B2C_TRANSFER_MOCK = `/mock${B2C_ROOT}/transfer`

export const B2C_ENDPOINTS = {
  list: `${B2C_TRANSFER_ROOT}/requests`,
  registryList: `${B2C_TRANSFER_ROOT}/registry/list`,
  registryListMock: `${B2C_TRANSFER_MOCK}/registry/list`,
  item: (id: string) => `${B2C_TRANSFER_ROOT}/requests/${id}`,
  registryTemplateFormat: `${B2C_TRANSFER_ROOT}/registry/template`,
  registry: `${B2C_TRANSFER_ROOT}/registry`, //временный
  registryUpload: `${B2C_TRANSFER_ROOT}/registry/upload`, //временный
  registryConfirm: `${B2C_TRANSFER_ROOT}/registry/confirm`,
  // https://confluence.frhc.group/pages/viewpage.action?pageId=483066431
  transferConfirm: `${B2C_TRANSFER_ROOT}/confirm`,
  // https://confluence.frhc.group/pages/viewpage.action?pageId=483065889
  transferCheck: `${B2C_TRANSFER_ROOT}/check`,

  resendCode: `${B2C_ROOT}/resend-code`,
  // https://confluence.frhc.group/pages/viewpage.action?pageId=483066401
  listParticipants: `/sbp-participants`,
}

export const useGetB2CPaymentsList = () => {
  const { page, showCount } = useB2COneTimePaymentsTableState()

  const {
    dateRange: [dateFrom, dateTo],
    b2cState: transferState,
    b2cPaymentsType: paymentType,
  } = useB2CState()

  const params = { dateFrom, dateTo, transferState }

  const queryParams = omitRequestParams({
    page,
    showCount,
    ...params,
    transferState: params.transferState === 'ALL' ? undefined : params.transferState,
    payment_type: paymentType === 'ALL' ? undefined : paymentType,
  })

  return useQuery({
    placeholderData: keepPreviousData,
    queryKey: ['b2c_list', queryParams],
    queryFn: () =>
      apiService.get<TableData<B2CPaymentsRequest>>(B2C_ENDPOINTS.list, {
        queryParams,
      }),
  })
}

export const useGetB2CRegistriesList = () => {
  const { page, showCount } = useB2CRegistryPaymentsTableState()

  const {
    dateRange: [dateFrom, dateTo],
    b2cRegistryState: transferState,
  } = useB2CState()

  const params = { dateFrom, dateTo, transferState }

  const queryParams = omitRequestParams({
    page,
    showCount,
    ...params,
    transferState: params.transferState === 'ALL' ? undefined : params.transferState,
  })

  return useQuery({
    placeholderData: keepPreviousData,
    queryKey: ['b2c_registry_list', queryParams],
    queryFn: () =>
      apiService.get<TableData<B2CRegistriesRequest>>(B2C_ENDPOINTS.registryList, {
        queryParams,
      }),
  })
}

export const useGetB2CRegistryOperations = (id: string) => {
  const {
    page,
    showCount,
    dateRange: [dateFrom, dateTo],
    b2cState: transferState,
  } = useB2CRegistryState()

  const params = { dateFrom, dateTo, transferState }

  const queryParams = omitRequestParams({
    id,
    page,
    showCount,
    ...params,
    transferState: params.transferState === 'ALL' ? undefined : params.transferState,
  })

  return useQuery({
    queryKey: ['b2c_registry_operations', queryParams],
    queryFn: () =>
      apiService.get<TableData<B2CPaymentsRequest>>(B2C_ENDPOINTS.registry, {
        queryParams,
      }),
  })
}

export const useGetB2CPaymentsItem = (id: string) =>
  useQuery({
    queryKey: ['b2c_item', { id }],
    queryFn: () => apiService.get<B2CPaymentsRequest>(B2C_ENDPOINTS.item(id)),
  })

export const useGetRegistryTemplate = () =>
  useMutation({
    mutationFn: (queryParams: { formatRegistry: string }) =>
      apiService.get<Blob>(B2C_ENDPOINTS.registryTemplateFormat, {
        queryParams: omitRequestParams(queryParams),
        axiosParams: { responseType: 'blob' },
      }),
  })

export const useGetSbpParticipants = () =>
  useQuery({
    placeholderData: keepPreviousData,
    retry: 2,
    queryKey: ['sbp_patricipants'],
    queryFn: () => apiService.get<{ participants: Array<SbpParticipant> }>(B2C_ENDPOINTS.listParticipants),
  })

export const useMutateB2cTransferCheck = () =>
  useMutation<
    AxiosResponse<B2CTransferCheckSuccessResponse>,
    AxiosError<B2CTransferCheckErrorResponse>,
    B2CTransferRequestForm,
    unknown
  >({
    mutationFn: (bodyParams: B2CTransferRequestForm) =>
      apiService.post<B2CTransferCheckSuccessResponse>(B2C_ENDPOINTS.transferCheck, { bodyParams }),
  })

export const useMutateB2CResendCode = () =>
  useMutation({
    mutationFn: () => apiService.post<{ message: string }>(B2C_ENDPOINTS.resendCode),
  })

export const useMutateB2cTransferConfirm = () =>
  useMutation<
    AxiosResponse<B2CTransferConfirmResponse>,
    AxiosError<B2CTransferConfirmErrorResponse>,
    B2CTransferConfirmForm & { id: number },
    unknown
  >({
    mutationFn: (bodyParams) => apiService.post(B2C_ENDPOINTS.transferConfirm, { bodyParams }),
  })

export const useMutateB2cRegistryInit = () =>
  useMutation<
    AxiosResponse<B2CRegistryInitSuccessResponse>,
    AxiosError<B2CRegistryInitErrorResponse>,
    FormData,
    unknown
  >({
    mutationFn: (formData: FormData) =>
      apiService.post<B2CRegistryInitSuccessResponse>(B2C_ENDPOINTS.registryUpload, {
        bodyParams: formData,
        headerParams: {
          'Content-Type': 'multipart/form-data',
        },
      }),
  })

export const useMutateB2cRegistryConfirm = () =>
  useMutation<
    AxiosResponse<B2CRegistryConfirmSuccessResponse>,
    AxiosError<B2CRegistryConfirmErrorResponse>,
    B2CRegistryConfirmForm & { id: number },
    unknown
  >({
    mutationFn: (bodyParams) => apiService.post(B2C_ENDPOINTS.registryConfirm, { bodyParams }),
  })
